import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { API_URL } from "../config";
import { toast } from "sonner";

const inputCls =
  "w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C87A38] focus:border-transparent";

const api = (token) => axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/login`, { email, password });
      localStorage.setItem("zum_admin_token", res.data.access_token);
      onLogin(res.data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
      <h2 className="text-2xl font-semibold font-heading">Owner login</h2>
      <p className="mt-2 text-sm text-[#525252]">Log in to manage flooring colours, projects and quote enquiries.</p>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
        <div>
          <label htmlFor="a_email" className="block text-sm font-semibold text-[#4A3018] mb-1.5">Email</label>
          <input id="a_email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" />
        </div>
        <div>
          <label htmlFor="a_pass" className="block text-sm font-semibold text-[#4A3018] mb-1.5">Password</label>
          <input id="a_pass" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" />
        </div>
        <button type="submit" disabled={loading} className="w-full rounded-full bg-[#C87A38] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#A6632B] disabled:opacity-60" data-testid="admin-login-submit">
          {loading ? "Logging in…" : "Log In"}
        </button>
      </form>
    </div>
  );
}

function ColourForm({ initial, token, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || { name: "", code: "", thickness: "7.5 mm", style: "straight", light: "#D9B98A", dark: "#8A5A2B", image_url: "" });
  const [saving, setSaving] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    try {
      const res = await api(token).post("/uploads", data);
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, image_url: form.image_url || null };
      if (initial?.id) await api(token).put(`/colours/${initial.id}`, payload);
      else await api(token).post("/colours", payload);
      toast.success("Colour saved");
      onSaved();
    } catch {
      toast.error("Could not save colour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-[#E5E5E5] rounded-xl p-6 bg-[#FAFAFA] space-y-4" data-testid="colour-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input className={inputCls} placeholder="Colour name (e.g. Natural Oak)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="colour-form-name" />
        <input className={inputCls} placeholder="Product code (e.g. ZF-206)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required data-testid="colour-form-code" />
        <input className={inputCls} placeholder="Thickness (e.g. 7.5 mm)" value={form.thickness || ""} onChange={(e) => setForm({ ...form, thickness: e.target.value })} data-testid="colour-form-thickness" />
        <select className={inputCls} value={form.style || "straight"} onChange={(e) => setForm({ ...form, style: e.target.value })} data-testid="colour-form-style">
          <option value="straight">Straight lay</option>
          <option value="herringbone">Herringbone</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4 items-end">
        <div>
          <label className="block text-xs font-semibold text-[#4A3018] mb-1">Light tone</label>
          <input type="color" className="w-full h-10 rounded border border-[#E5E5E5] cursor-pointer" value={form.light} onChange={(e) => setForm({ ...form, light: e.target.value })} data-testid="colour-form-light" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#4A3018] mb-1">Dark tone (grain)</label>
          <input type="color" className="w-full h-10 rounded border border-[#E5E5E5] cursor-pointer" value={form.dark} onChange={(e) => setForm({ ...form, dark: e.target.value })} data-testid="colour-form-dark" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#4A3018] mb-1">Photo (optional — replaces the generated swatch)</label>
        <input type="file" accept="image/*" onChange={upload} className="text-sm" data-testid="colour-form-image" />
        {form.image_url && <p className="text-xs text-[#525252] mt-1">Image attached ✓</p>}
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="rounded-full bg-[#C87A38] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A6632B] disabled:opacity-60" data-testid="colour-form-save">
          {saving ? "Saving…" : "Save Colour"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-[#E5E5E5] px-5 py-2.5 text-sm font-semibold hover:bg-black/5" data-testid="colour-form-cancel">Cancel</button>
      </div>
    </form>
  );
}

function ProjectForm({ initial, token, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || { suburb: "", colour: "", area: "", services: "", before_url: "", after_url: "", photos: [] });
  const [saving, setSaving] = useState(false);

  const upload = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    try {
      const res = await api(token).post("/uploads", data);
      setForm((f) => ({ ...f, [key]: res.data.url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const uploadExtra = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const data = new FormData();
      data.append("file", file);
      try {
        const res = await api(token).post("/uploads", data);
        setForm((f) => ({ ...f, photos: [...(f.photos || []), res.data.url] }));
      } catch {
        toast.error("Upload failed");
      }
    }
    if (files.length) toast.success("Photos uploaded");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, before_url: form.before_url || null, after_url: form.after_url || null, photos: form.photos || [] };
      if (initial?.id) await api(token).put(`/projects/${initial.id}`, payload);
      else await api(token).post("/projects", payload);
      toast.success("Project saved");
      onSaved();
    } catch {
      toast.error("Could not save project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-[#E5E5E5] rounded-xl p-6 bg-[#FAFAFA] space-y-4" data-testid="project-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input className={inputCls} placeholder="Suburb (e.g. Point Cook)" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} required data-testid="project-form-suburb" />
        <input className={inputCls} placeholder="Flooring colour" value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} data-testid="project-form-colour" />
        <input className={inputCls} placeholder="Approx. floor area (e.g. 65 m²)" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="project-form-area" />
        <input className={inputCls} placeholder="Services completed" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} data-testid="project-form-services" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-[#4A3018] mb-1">Before photo (optional)</label>
          <input type="file" accept="image/*" onChange={upload("before_url")} className="text-sm" data-testid="project-form-before" />
          {form.before_url && <p className="text-xs text-[#525252] mt-1">Attached ✓</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#4A3018] mb-1">After photo</label>
          <input type="file" accept="image/*" onChange={upload("after_url")} className="text-sm" data-testid="project-form-after" />
          {form.after_url && <p className="text-xs text-[#525252] mt-1">Attached ✓</p>}
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#4A3018] mb-1">More photos (optional)</label>
        <input type="file" accept="image/*" multiple onChange={uploadExtra} className="text-sm" data-testid="project-form-photos" />
        {(form.photos || []).length > 0 && <p className="text-xs text-[#525252] mt-1">{form.photos.length} extra photo{form.photos.length > 1 ? "s" : ""} attached ✓</p>}
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="rounded-full bg-[#C87A38] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A6632B] disabled:opacity-60" data-testid="project-form-save">
          {saving ? "Saving…" : "Save Project"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-[#E5E5E5] px-5 py-2.5 text-sm font-semibold hover:bg-black/5" data-testid="project-form-cancel">Cancel</button>
      </div>
    </form>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("zum_admin_token") || "");
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [colours, setColours] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editingColour, setEditingColour] = useState(undefined);
  const [editingProject, setEditingProject] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const loadAll = async (tok) => {
    try {
      const [q, c, p] = await Promise.all([
        api(tok).get("/quotes"),
        axios.get(`${API_URL}/colours`),
        axios.get(`${API_URL}/projects`),
      ]);
      setQuotes(q.data);
      setColours(c.data);
      setProjects(p.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("zum_admin_token");
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadAll(token);
    else setLoading(false);
  }, [token]);

  const deleteColour = async (id) => {
    try {
      await api(token).delete(`/colours/${id}`);
      toast.success("Colour removed");
      loadAll(token);
    } catch { toast.error("Could not delete"); }
  };

  const deleteProject = async (id) => {
    try {
      await api(token).delete(`/projects/${id}`);
      toast.success("Project removed");
      loadAll(token);
    } catch { toast.error("Could not delete"); }
  };

  if (!token) {
    return (
      <div data-testid="admin-page">
        <PageHeader label="Admin" title="Zum Flooring admin" testId="admin-header" />
        <Login onLogin={setToken} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 flex items-center gap-2 text-[#525252]" data-testid="admin-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }

  const tabs = [
    { id: "quotes", label: `Quote Enquiries (${quotes.length})` },
    { id: "colours", label: "Flooring Colours" },
    { id: "projects", label: "Recent Projects" },
  ];

  return (
    <div data-testid="admin-page">
      <PageHeader label="Admin" title="Zum Flooring admin" description="Manage your quote enquiries, flooring colours and recent projects." testId="admin-header" />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-center gap-2 mb-10">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-[#4A3018] text-white" : "border border-[#E5E5E5] text-[#525252] hover:bg-black/5"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={() => { localStorage.removeItem("zum_admin_token"); setToken(""); }}
            className="ml-auto text-sm font-medium text-[#525252] hover:text-[#C87A38]"
            data-testid="admin-logout"
          >
            Log out
          </button>
        </div>

        {tab === "quotes" && (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.length === 0 && <p className="text-[#525252]">No enquiries yet.</p>}
            {quotes.map((q) => (
              <article key={q.id} className="border border-[#E5E5E5] rounded-xl p-6 bg-white" data-testid={`admin-quote-${q.id}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <h3 className="font-semibold font-heading text-lg">{q.full_name} <span className="text-sm font-normal text-[#525252]">— {q.service}</span></h3>
                  <span className="text-xs text-[#A3A3A3] flex items-center gap-2">
                    {q.email_sent === true && <span className="rounded-full bg-green-50 text-green-700 px-2 py-0.5 font-medium" data-testid={`quote-email-sent-${q.id}`}>Emailed</span>}
                    {q.email_sent === false && <span className="rounded-full bg-red-50 text-red-700 px-2 py-0.5 font-medium" title={q.email_error} data-testid={`quote-email-failed-${q.id}`}>Email failed</span>}
                    {new Date(q.created_at).toLocaleString("en-AU")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-[#525252]">{q.phone} · {q.email} · {q.suburb}</p>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-[#525252]">
                  {q.area && <span>Area: {q.area} m²</span>}
                  {q.existing_floor && <span>Existing: {q.existing_floor}</span>}
                  {q.colour && <span>Colour: {q.colour}</span>}
                  {q.install_date && <span>Date: {q.install_date}</span>}
                </div>
                {q.message && <p className="mt-3 text-sm text-[#525252] border-t border-[#E5E5E5] pt-3">{q.message}</p>}
                {q.photos?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {q.photos.map((p) => (
                      <a key={p} href={`${process.env.REACT_APP_BACKEND_URL}${p}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#C87A38] underline" data-testid="admin-quote-photo">
                        View attachment
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {tab === "colours" && (
          <div data-testid="admin-colours-list">
            {editingColour === undefined ? (
              <button onClick={() => setEditingColour(null)} className="inline-flex items-center gap-2 rounded-full bg-[#C87A38] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A6632B]" data-testid="add-colour-button">
                <Plus className="w-4 h-4" /> Add Colour
              </button>
            ) : (
              <ColourForm initial={editingColour} token={token} onSaved={() => { setEditingColour(undefined); loadAll(token); }} onCancel={() => setEditingColour(undefined)} />
            )}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {colours.map((c) => (
                <div key={c.id} className="border border-[#E5E5E5] rounded-xl p-4 bg-white flex items-center gap-4" data-testid={`admin-colour-${c.code}`}>
                  <div className="w-14 h-14 rounded-lg shrink-0" style={{ background: `linear-gradient(135deg, ${c.light}, ${c.dark})` }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold font-heading truncate">{c.name}</p>
                    <p className="text-xs text-[#525252]">{c.code}</p>
                  </div>
                  <button onClick={() => setEditingColour(c)} className="p-2 rounded-md hover:bg-black/5" aria-label={`Edit ${c.name}`} data-testid={`edit-colour-${c.code}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteColour(c.id)} className="p-2 rounded-md hover:bg-red-50 text-red-600" aria-label={`Delete ${c.name}`} data-testid={`delete-colour-${c.code}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "projects" && (
          <div data-testid="admin-projects-list">
            {editingProject === undefined ? (
              <button onClick={() => setEditingProject(null)} className="inline-flex items-center gap-2 rounded-full bg-[#C87A38] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A6632B]" data-testid="add-project-button">
                <Plus className="w-4 h-4" /> Add Project
              </button>
            ) : (
              <ProjectForm initial={editingProject} token={token} onSaved={() => { setEditingProject(undefined); loadAll(token); }} onCancel={() => setEditingProject(undefined)} />
            )}
            <div className="mt-6 space-y-3">
              {projects.length === 0 && <p className="text-[#525252]">No projects yet — add your first completed job.</p>}
              {projects.map((p) => (
                <div key={p.id} className="border border-[#E5E5E5] rounded-xl p-4 bg-white flex items-center gap-4" data-testid={`admin-project-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold font-heading">{p.suburb}</p>
                    <p className="text-xs text-[#525252] truncate">{[p.colour, p.area, p.services].filter(Boolean).join(" · ")}</p>
                  </div>
                  <button onClick={() => setEditingProject(p)} className="p-2 rounded-md hover:bg-black/5" aria-label="Edit project" data-testid="edit-project-button">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteProject(p.id)} className="p-2 rounded-md hover:bg-red-50 text-red-600" aria-label="Delete project" data-testid="delete-project-button">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
